import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import ApplyInfo from '@components/position/jd/apply/applyInfo';
import JDModals, { JDModalType } from '@components/position/jd/apply/modals';
import Share from '@components/position/jd/apply/share';
import BookmarkButton from '@components/position/jd/apply/bookmarkButton';
import ApplyButton from '@components/position/jd/apply/applyButton';
import { applicationKeys, fetchRequiredItems, useApplyVerification } from '@repository/match';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { PositionDetailVO } from '@domain/vo/position/jd/PositionDetailVO';
import { JobApplicationProcessType } from '@domain/constant/jobApplicationProcessType';

import { queryClient } from '@utils/queryClient';
import UrlUtil from '@utils/urlUtil';
import useOnMount from '@utils/hooks/useOnMount';

import classnames from 'classnames/bind';
import styles from './apply.module.scss';
const cx = classnames.bind(styles);

interface ApplyProps {
  positionSn?: number;
  positionDetail: PositionDetailVO;
}

const Apply = ({ positionSn, positionDetail }: ApplyProps) => {
  const [applyState, setApplyState] = useState<JobApplicationProcessType | null>(null);
  const [modalState, setModalState] = useState<JDModalType>(null);
  const { query } = useRouter();
  const Toast = useToast();

  const { data: userInfo, isLoading: isLoadingUserInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  useOnMount(() => {
    setModalState(null);
  });

  const { mutateAsync: applyVerificationMutateAsync } = useApplyVerification(
    async () => {
      if (!positionSn) return;

      const { isProfileComplete } = await fetchRequiredItems(positionSn);
      // 프로필 지원 모달 Open
      if (isProfileComplete) {
        await queryClient.refetchQueries([...applicationKeys, positionSn, 'additionalItems']);
        setModalState('EASY_APPLY');
      } else {
        setModalState('PROFILE_REQUIRED');
      }
    },
    (err) => {
      let content: string;

      switch (err?.response?.data?.errorCode) {
        case 'B002':
          setModalState('TERMS');
          return;
        case 'B902':
          content = '삭제된 포지션 입니다.';
          break;
        case 'B903':
          content = '종료된 포지션 입니다.';
          break;
        case 'B904':
          content = '마감된 포지션 입니다.';
          break;
        case 'B905':
          content = '이미 지원한 포지션 입니다.';
          break;
        case 'B906':
          content = '이미 채용 전형이 진행중인 포지션입니다.';
          break;
        case 'B907': //지원 가능한 횟수를 초과하였습니다.
          setModalState('APPLY_EXCEED');
          return;
        case 'B908':
          content = '지원 조건이 변경되었습니다. 다시 지원해 주세요.';
          break;
        case 'B901': //통신 오류
        default:
          setModalState('ERROR');
          return;
      }

      Toast({
        type: 'error',
        iconType: 'info',
        content,
      });
    },
  );

  useEffect(() => {
    const { apply } = query;
    if (!apply || !positionSn) return;

    if (apply === 'profile') {
      applyVerificationMutateAsync(positionSn);
      UrlUtil.removeQueryStringAndReplace('apply');
    }
  }, [query, positionSn, applyVerificationMutateAsync]);

  const handleApply = async () => {
    if (!positionSn || isLoadingUserInfo) return;

    if (checkLogin()) {
      await applyVerificationMutateAsync(positionSn);
    }
  };

  const checkLogin = () => {
    if (!isLogin) {
      setModalState('LOGIN');
      return false;
    }

    return true;
  };

  useEffect(() => {
    setApplyState(positionDetail?.stateInfo?.jobApplicationProcessType ?? null);
  }, [positionDetail]);

  return (
    <div className={cx('stickyArea')}>
      {/* [D] 등록일 마감일 영역 */}
      <ApplyInfo positionDetail={positionDetail} />

      <div className={cx('btnArea')}>
        {/* [D] 지원하기 버튼 영역 */}
        <ApplyButton
          positionSn={positionSn}
          positionDetail={positionDetail}
          applyState={applyState}
          setModalState={setModalState}
          handleApply={handleApply}
        />

        {/* [D] 북마크 버튼 영역 */}
        <BookmarkButton positionSn={positionSn} positionDetail={positionDetail} setModalState={setModalState} />
      </div>

      {/* [D] 공유하기 영역 */}
      <Share positionSn={positionSn} />

      {/* [D] JD 모달 영역 */}
      <JDModals
        positionSn={positionSn}
        modalState={modalState}
        setModalState={setModalState}
        setApplyState={setApplyState}
        positionDetail={positionDetail}
        onTermsConfirm={handleApply}
      />
    </div>
  );
};

export default Apply;
